import React from 'react';
import { Box, Button, Typography, Card, CardContent, Fade } from '@mui/material';
import { Assignment, Psychology, ArrowBack, OpenInNew } from '@mui/icons-material';
import config from '../config';

interface AssessmentLinksProps {
  track: 'cloud' | 'data';
  onBack: () => void;
}

const AssessmentLinks: React.FC<AssessmentLinksProps> = ({ track, onBack }) => {
  const assessments = track === 'data'
    ? [
        {
          title: 'Data & ML Assessment',
          description: 'Complete the Data & Machine Learning technical assessment',
          url: config.assessments.data.dataML,
          icon: <Assignment sx={{ fontSize: 40 }} />,
          color: '#10b981'
        },
        {
          title: 'Logical Reasoning Assessment',
          description: 'Complete the logical reasoning and problem-solving assessment',
          url: config.assessments.data.logical,
          icon: <Psychology sx={{ fontSize: 40 }} />,
          color: '#34d399'
        }
      ]
    : [
        {
          title: 'Cloud Engineering Assessment',
          description: 'Complete the Cloud Engineering technical assessment',
          url: config.assessments.cloud.cloud,
          icon: <Assignment sx={{ fontSize: 40 }} />,
          color: '#3b82f6'
        },
        {
          title: 'Logical Reasoning Assessment',
          description: 'Complete the logical reasoning and problem-solving assessment',
          url: config.assessments.cloud.logical,
          icon: <Psychology sx={{ fontSize: 40 }} />,
          color: '#60a5fa'
        }
      ];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Fira Code", "JetBrains Mono", monospace',
            color: 'rgba(103,232,249,0.6)',
            fontSize: '0.75rem',
            letterSpacing: '1px',
            mb: 1,
            display: 'block',
          }}
        >
          {'>'} STEP 2: TECHNICAL ASSESSMENTS
        </Typography>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            mb: 1,
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            color: '#f1f5f9',
            letterSpacing: '-0.5px',
          }}
        >
          Your Assessments
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{
            color: 'text.secondary',
            fontFamily: '"Fira Code", "JetBrains Mono", monospace',
            fontSize: '0.9rem',
          }}
        >
          <Box component="span" sx={{ color: track === 'cloud' ? '#60a5fa' : '#34d399' }}>
            {track === 'cloud' ? '// Cloud Engineer Track' : '// Data & ML Engineer Track'}
          </Box>
        </Typography>
      </Box>

      {/* Proctoring Warning */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(245,158,11,0.05) 100%)',
          border: '1px solid rgba(251,191,36,0.3)',
          boxShadow: '0 8px 32px rgba(251,191,36,0.15)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(251,191,36,0.25)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent 0%, #f59e0b 30%, #fbbf24 50%, #f59e0b 70%, transparent 100%)',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(251,191,36,0.2) 0%, rgba(245,158,11,0.15) 100%)',
              border: '1px solid rgba(251,191,36,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              flexShrink: 0,
            }}
          >
            ⚠️
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: '1.1rem',
                color: '#fbbf24',
                letterSpacing: '0.5px',
              }}
            >
              EXAMINATION MONITORING NOTICE
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                color: 'rgba(251,191,36,0.7)',
                fontSize: '0.7rem',
                letterSpacing: '0.3px',
              }}
            >
              Please read carefully before proceeding
            </Typography>
          </Box>
        </Box>

        <Box sx={{ pl: 7 }}>
          <Typography variant="body2" component="div" sx={{ lineHeight: 2, color: 'text.primary' }}>
            <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', '& li': { mb: 1.5 } }}>
              <li>
                <Box component="span" sx={{ fontWeight: 600, color: '#fbbf24' }}>Browser and tab switch activity will be monitored</Box> during the examination once you are redirected to the assessment page
              </li>
              <li>
                Candidates are requested to <Box component="span" sx={{ fontWeight: 600, color: '#fbbf24' }}>always keep the examination in full screen mode</Box> to avoid repeated warnings
              </li>
              <li>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: '#ef4444',
                    background: 'rgba(239,68,68,0.1)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    border: '1px solid rgba(239,68,68,0.2)',
                  }}
                >
                  Academic dishonesty will result in immediate disqualification
                </Box>
              </li>
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Important Instructions */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(34,211,238,0.06) 0%, rgba(6,182,212,0.03) 100%)',
          border: '1px solid rgba(34,211,238,0.2)',
          boxShadow: '0 4px 20px rgba(34,211,238,0.08)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(148,163,184,0.2) 50%, transparent 100%)',
            opacity: 0.6,
          }
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: '0.95rem',
            color: '#22d3ee',
            letterSpacing: '0.3px',
          }}
        >
          📋 Important Instructions
        </Typography>
        <Typography variant="body2" component="div" sx={{ lineHeight: 2, color: 'text.primary' }}>
          <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', '& li': { mb: 1 } }}>
            <li>You must complete <Box component="span" sx={{ fontWeight: 600, color: '#f1f5f9' }}>both</Box> assessments</li>
            <li>Click on each assessment card to open the assessment in a new tab</li>
            <li>Complete the assessments within the alloted time</li>
            <li>Ensure you have all the required documentation mentioned in the eligibility check</li>
          </Box>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
        {assessments.map((assessment, index) => (
          <Fade in timeout={600 + index * 200} key={assessment.title}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 32px rgba(${track === 'cloud' ? '34,211,238' : '196,181,253'},0.3)`,
                }
              }}
              onClick={() => window.open(assessment.url, '_blank')}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${assessment.color}20, ${assessment.color}10)`,
                      color: assessment.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {assessment.icon}
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {assessment.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      {assessment.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNew />}
                      sx={{
                        borderColor: 'rgba(245,158,11,0.5)',
                        color: '#fbbf24',
                        borderWidth: 2,
                        '&:hover': {
                          borderColor: 'rgba(245,158,11,0.8)',
                          backgroundColor: 'rgba(245,158,11,0.1)',
                          transform: 'translateY(-2px)',
                        }
                      }}
                    >
                      Open Assessment
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            px: 4,
            py: 1.5,
            borderColor: 'rgba(245,158,11,0.5)',
            color: '#fbbf24',
            borderWidth: 2,
            '&:hover': {
              borderColor: 'rgba(245,158,11,0.8)',
              backgroundColor: 'rgba(245,158,11,0.1)',
              transform: 'translateY(-2px)',
            }
          }}
        >
          Back to Home
        </Button>
      </Box>
    </Box>
  );
};

export default AssessmentLinks;

